import { cloneDeep } from 'lodash/fp';
import get from 'lodash/get';

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { CutoutState } from './cutout.types';

import {
  ApplianceId,
  ApplianceQtyType,
  CutoutId,
  SelectedApplianceCutouts,
  SelectedCustomCutouts,
} from '~/core/types/appliances';
import { decById, incById } from '~/core/utils';

const initialState: CutoutState = {
  customCutouts: { selected: {} },
  applianceCutouts: { selected: {} },
};

const cutoutSlice = createSlice({
  name: 'cutout',
  initialState,
  reducers: {
    increaseCustomCutouts: (
      state,
      {
        payload: { cutoutId, qtyType },
      }: PayloadAction<{ cutoutId: CutoutId; qtyType: ApplianceQtyType }>
    ) => {
      const currentState = cloneDeep(state.customCutouts);

      state.customCutouts.selected[cutoutId] = incById(
        get(state.customCutouts.selected, cutoutId),
        qtyType
      );
      state.customCutouts.previousSelected = currentState;
    },
    decreaseCustomCutouts: (
      state,
      {
        payload: { cutoutId, qtyType },
      }: PayloadAction<{ cutoutId: CutoutId; qtyType: ApplianceQtyType }>
    ) => {
      const currentState = cloneDeep(state.customCutouts);

      state.customCutouts.selected[cutoutId] = decById(
        get(state.customCutouts.selected, cutoutId),
        qtyType
      );
      state.customCutouts.previousSelected = currentState;
    },
    setCustomCutouts: (
      state,
      action: PayloadAction<SelectedCustomCutouts | undefined>
    ) => {
      state.customCutouts = action.payload ?? initialState.customCutouts;
    },
    increaseApplianceCutouts: (
      state,
      {
        payload: { cutoutId, applianceId },
      }: PayloadAction<{
        cutoutId: CutoutId;
        applianceId: ApplianceId;
      }>
    ) => {
      const currentState = cloneDeep(state.applianceCutouts);

      state.applianceCutouts.selected[cutoutId] = incById(
        get(state.applianceCutouts.selected, cutoutId),
        applianceId
      );
      state.applianceCutouts.previousSelected = currentState;
    },
    decreaseApplianceCutouts: (
      state,
      {
        payload: { cutoutId, applianceId },
      }: PayloadAction<{
        cutoutId: CutoutId;
        applianceId: ApplianceId;
      }>
    ) => {
      const currentState = cloneDeep(state.applianceCutouts);

      state.applianceCutouts.selected[cutoutId] = decById(
        get(state.applianceCutouts.selected, cutoutId),
        applianceId
      );
      state.applianceCutouts.previousSelected = currentState;
    },
    resetAllCookingApplianceCutouts: (
      state,
      {
        payload: { cooktopCutoutId },
      }: PayloadAction<{
        cooktopCutoutId: CutoutId;
      }>
    ) => {
      const currentState = cloneDeep(state.applianceCutouts);

      const updatedApplianceCutouts = { ...state.applianceCutouts.selected };

      delete updatedApplianceCutouts[cooktopCutoutId];

      state.applianceCutouts.selected = updatedApplianceCutouts;
      state.applianceCutouts.previousSelected = currentState;
    },
    resetCookingApplianceCutoutById: (
      state,
      {
        payload: { applianceId, cooktopCutoutId },
      }: PayloadAction<{
        applianceId: ApplianceId;
        cooktopCutoutId: CutoutId;
      }>
    ) => {
      const currentState = cloneDeep(state.applianceCutouts);

      const updatedCookingApplianceCutouts = {
        ...state.applianceCutouts.selected[cooktopCutoutId],
      };

      delete updatedCookingApplianceCutouts[applianceId];

      state.applianceCutouts.selected[cooktopCutoutId] =
        updatedCookingApplianceCutouts;
      state.applianceCutouts.previousSelected = currentState;
    },
    resetCustomCutoutById: (
      state,
      {
        payload: { cutoutIdToRemove },
      }: PayloadAction<{
        cutoutIdToRemove: CutoutId;
      }>
    ) => {
      const currentState = cloneDeep(state.customCutouts);

      const updatedSelectedState = { ...state.customCutouts.selected };

      delete updatedSelectedState[cutoutIdToRemove];

      state.customCutouts.selected = updatedSelectedState;
      state.customCutouts.previousSelected = currentState;
    },
    setApplianceCutouts: (
      state,
      action: PayloadAction<SelectedApplianceCutouts | undefined>
    ) => {
      state.applianceCutouts = action.payload ?? initialState.applianceCutouts;
    },
  },
});

export const {
  decreaseApplianceCutouts,
  decreaseCustomCutouts,
  increaseApplianceCutouts,
  increaseCustomCutouts,
  resetAllCookingApplianceCutouts,
  resetCookingApplianceCutoutById,
  resetCustomCutoutById,
  setApplianceCutouts,
  setCustomCutouts,
} = cutoutSlice.actions;

export default cutoutSlice.reducer;
